// src/app/time-attendance/components/offtime/offtime.component.ts

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BehaviorSubject, Observable, Subject, forkJoin, of, throwError, timer } from 'rxjs';
import {
  catchError,
  finalize,
  map,
  retry,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { OfftimeService } from './services/offtime.service';
import {
  AddMoreDayOffRequestsBody,
  ApiPagedResponse,
  ApprovalFilter,
  CreateDayOffRequestBody,
  CurrentUserDto,
  EmployeeLeaveBalanceDto,
  LeaveType,
  OfftimeGroupOrderBy,
  OfftimeGroupScope,
  OfftimePayCodeDto,
  OfftimeRequestFileDto,
  OfftimeRequestGroupDetailsDto,
  OfftimeRequestGroupListDto,
  OfftimeUserListDto,
  PatchDayOffRequestBody,
} from './models/offtime.model';
import { SnackbarService } from '../services/snackbar.service';
import { AccountService } from '../services/account.service';
import { RequestFilePreviewComponent } from './components/request-file-preview/request-file-preview.component';

dayjs.extend(utc);

type OfftimeTab = 'Create' | 'Pending' | 'Approved' | 'Rejected' | 'Cancelled';
type SelectionMode = 'range' | 'multi';
type ToastKind = 'success' | 'error' | 'info';
type RequestStatus = 'Pending' | 'Approved' | 'Rejected' | 'Cancelled';

interface OfftimeListFilter {
  isApproved: ApprovalFilter;
  isCanceled?: boolean;
  orderBy?: OfftimeGroupOrderBy;
  startDate?: string;
  endDate?: string;
}

interface TimeOptionVm {
  minutes: number;
  label: string;
}

interface SelectedDayVm {
  date: dayjs.Dayjs;
  startMinutes: number;
  endMinutes: number;
}

interface EditDayVm {
  id: number;
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
  timeZoneId: string;
  isDirty: boolean;
  isCancelled: boolean;
}

interface AddedDayVm {
  clientId: string;
  start: dayjs.Dayjs;
  end: dayjs.Dayjs;
  timeZoneId: string;
  isNew: boolean;
}

interface EditStateVm {
  groupId: number;
  employeeId: number | null;
  payCodeId: number;
  reason: string;
  status: RequestStatus;
  scope: OfftimeGroupScope;
  days: EditDayVm[];
  addedDays: AddedDayVm[];
  files: OfftimeRequestFileDto[];
}

interface BalanceImpactVm {
  leaveType: LeaveType;
  currentBalance: number;
  requestedHours: number;
  allowNegativeBalance: boolean;
}

type OfftimeViewMode = 'list' | 'details';

interface OfftimeTabCountVm {
  create: number;
  pending: number;
  approved: number;
  rejected: number;
  cancelled: number;
}

interface RequestCardVm {
  group: OfftimeRequestGroupListDto;
  scope: OfftimeGroupScope;
  status: RequestStatus;
}

@Component({
  selector: 'app-offtime',
  standalone: true,
  imports: [CommonModule, FormsModule, RequestFilePreviewComponent],
  templateUrl: './offtime.component.html',
  styleUrls: ['./offtime.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OfftimeComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly busy$ = new BehaviorSubject<boolean>(false);

  viewMode: OfftimeViewMode = 'list';

  tabCounts: OfftimeTabCountVm = {
    create: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
    cancelled: 0,
  };

  activeTab: OfftimeTab = 'Create';

  isBusy = false;

  toastMessage: string | null = null;
  toastKind: ToastKind = 'info';
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  modalMessage: string | null = null;
  modalKind: ToastKind = 'info';
  private modalTimer: ReturnType<typeof setTimeout> | null = null;

  payCodes$: Observable<OfftimePayCodeDto[]> = of([]);
  payCodes: OfftimePayCodeDto[] = [];
  selectedPayCodeId: number | null = null;

  selectionMode: SelectionMode = 'range';

  calendarMonth = dayjs().startOf('month');
  calendarCells: Array<dayjs.Dayjs | null> = [];

  isMonthPickerOpen = false;
  pickerYear = dayjs().year();
  pickerMonthIndex = dayjs().month();
  readonly maxFutureYears = 15;

  selectedDays: SelectedDayVm[] = [];
  private rangeStart: dayjs.Dayjs | null = null;

  defaultStartMinutes = 8 * 60;
  defaultEndMinutes = 17 * 60;

  isPerDayEditorOpen = false;
  createReason = '';
  createEmployeeId: number | null = null;
  users: OfftimeUserListDto[] = [];
  currentUserDetail: CurrentUserDto | null = null;
  timeOptions: TimeOptionVm[] = this.buildTimeOptions();

  editAddDate = '';
  editAddStartMinutes = 8 * 60;
  editAddEndMinutes = 17 * 60;

  pageSize = 10;

  readonly offtimeOrderByOptions: Array<{ value: OfftimeGroupOrderBy; label: string }> = [
    { value: 'startDateDesc', label: 'Start Date: Newest First' },
    { value: 'startDateAsc', label: 'Start Date: Oldest First' },
    { value: 'approvedAtDesc', label: 'Approved Date: Newest First' },
    { value: 'approvedAtAsc', label: 'Approved Date: Oldest First' },
  ];

  offtimeOrderBy: OfftimeGroupOrderBy = 'startDateDesc';
  offtimeStartDate = '';
  offtimeEndDate = '';

  pendingMinePage = 1;
  pendingAllPage = 1;

  approvedMinePage = 1;
  approvedAllPage = 1;

  rejectedMinePage = 1;
  rejectedAllPage = 1;

  cancelledMinePage = 1;
  cancelledAllPage = 1;

  pendingMineResp: ApiPagedResponse<OfftimeRequestGroupListDto[]> | null = null;
  pendingAllResp: ApiPagedResponse<OfftimeRequestGroupListDto[]> | null = null;

  approvedMineResp: ApiPagedResponse<OfftimeRequestGroupListDto[]> | null = null;
  approvedAllResp: ApiPagedResponse<OfftimeRequestGroupListDto[]> | null = null;

  rejectedMineResp: ApiPagedResponse<OfftimeRequestGroupListDto[]> | null = null;
  rejectedAllResp: ApiPagedResponse<OfftimeRequestGroupListDto[]> | null = null;

  cancelledMineResp: ApiPagedResponse<OfftimeRequestGroupListDto[]> | null = null;
  cancelledAllResp: ApiPagedResponse<OfftimeRequestGroupListDto[]> | null = null;

  isEditOpen = false;
  editState: EditStateVm | null = null;
  selectedAttachmentFiles: File[] = [];
  selectedCreateFiles: File[] = [];

  confirmOpen = false;
  confirmTitle = 'Confirm';
  confirmMessage = '';
  confirmOkText = 'Yes';
  confirmCancelText = 'No';
  confirmKind: 'default' | 'danger' = 'default';

  private snackbarService = inject(SnackbarService);
  private offtimeService = inject(OfftimeService);
  accountService = inject(AccountService);

  private cdr = inject(ChangeDetectorRef);
  private confirmResolver: ((value: boolean) => void) | null = null;

  get isAdmin(): boolean {
    return (this.accountService.user?.roles ?? []).some(
      (role) => role.name.toLowerCase() === 'admin',
    );
  }

  get selectedCreateEmployee(): OfftimeUserListDto | CurrentUserDto | null {
    const currentUserId = this.currentUserDetail?.id ?? null;

    if (
      this.createEmployeeId === null ||
      this.createEmployeeId === currentUserId
    ) {
      return this.currentUserDetail;
    }

    if (this.isAdmin) {
      return this.users.find((u) => u.id === this.createEmployeeId) ?? null;
    }

    return this.currentUserDetail;
  }

  get selectedCreatePayCode(): OfftimePayCodeDto | null {
    return this.payCodes.find((pc) => pc.id === this.selectedPayCodeId) ?? null;
  }

  get selectedCreateLeaveType(): LeaveType | null {
    return this.getLeaveTypeFromPayCodeName(this.selectedCreatePayCode?.name ?? null);
  }

  get createRequestedHours(): number {
    return this.sumRequestDateHours(
      this.selectedDays.map((day) => ({
        totalHours: this.getSelectedDayHours(day),
      })),
    );
  }

  get createBalanceImpact(): BalanceImpactVm | null {
    const employee = this.selectedCreateEmployee;
    const leaveType = this.selectedCreateLeaveType;

    if (!employee || !leaveType) {
      return null;
    }

    const currentBalance = this.getAvailableHoursFromBalances(
      employee.employeeLeaveBalances,
      leaveType,
    );
    const requestedHours = this.createRequestedHours;
    const allowNegativeBalance = this.getAllowNegativeBalanceForEmployee(employee, leaveType);

    return {
      leaveType,
      currentBalance,
      requestedHours,
      allowNegativeBalance,
    };
  }

  get canSubmitForBalance(): boolean {
    const impact = this.createBalanceImpact;

    if (!impact) {
      return true;
    }

    if (impact.allowNegativeBalance) {
      return true;
    }

    return impact.currentBalance - impact.requestedHours >= 0;
  }

  ngOnInit(): void {
    this.busy$.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.isBusy = value;
      this.safeMarkForCheck();
    });

    forkJoin({
      users: this.offtimeService.getUsers().pipe(
        catchError(() => of([] as OfftimeUserListDto[])),
      ),
      currentUser: this.offtimeService.getCurrentUser(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ users, currentUser }) => {
        this.users = users ?? [];
        this.currentUserDetail = currentUser ?? null;

        if (!this.isAdmin && this.currentUserDetail) {
          this.createEmployeeId = this.currentUserDetail.id;
        }

        this.safeMarkForCheck();
      });

    this.loadPayCodes();
    this.preloadRequestCounts();

    this.calendarMonth = this.clampToMinMonth(this.calendarMonth.startOf('month'));
    this.syncPickerToCalendar();
    this.rebuildCalendarCells();
  }

  private loadPayCodes(): void {
    this.payCodes$ = this.offtimeService.getPayCodes().pipe(
      map((payCodes) => payCodes ?? []),
      catchError((error) => {
        console.error('Failed loading offtime pay codes:', error);
        return of([] as OfftimePayCodeDto[]);
      }),
    );

    this.payCodes$
      .pipe(takeUntil(this.destroy$))
      .subscribe((payCodes) => {
        this.payCodes = payCodes ?? [];

        if (
          this.selectedPayCodeId !== null &&
          !this.payCodes.some((payCode) => payCode.id === this.selectedPayCodeId)
        ) {
          this.selectedPayCodeId = null;
        }

        if (!this.selectedPayCodeId && this.payCodes.length) {
          this.selectedPayCodeId = this.payCodes[0].id;
        }

        this.safeMarkForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    if (this.modalTimer) {
      clearTimeout(this.modalTimer);
      this.modalTimer = null;
    }

    if (this.confirmResolver) {
      this.confirmResolver(false);
      this.confirmResolver = null;
    }
  }

  private safeMarkForCheck(): void {
    this.cdr.markForCheck();
  }

  private setBusy(isBusy: boolean): void {
    this.busy$.next(isBusy);
  }

  private withBusy<T>(source$: Observable<T>): Observable<T> {
    this.setBusy(true);

    return source$.pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.setBusy(false);
        this.safeMarkForCheck();
      }),
    );
  }

  clearToast(): void {
    this.toastMessage = null;

    if (this.toastTimer) {
      clearTimeout(this.toastTimer);
      this.toastTimer = null;
    }

    this.safeMarkForCheck();
  }

  private showModalMessage(message: string, kind: ToastKind = 'info', ms = 4500): void {
    this.modalMessage = message;
    this.modalKind = kind;
    this.safeMarkForCheck();

    if (this.modalTimer) {
      clearTimeout(this.modalTimer);
    }

    this.modalTimer = setTimeout(() => {
      this.modalMessage = null;
      this.modalTimer = null;
      this.safeMarkForCheck();
    }, ms);
  }

  clearModalMessage(): void {
    this.modalMessage = null;

    if (this.modalTimer) {
      clearTimeout(this.modalTimer);
      this.modalTimer = null;
    }

    this.safeMarkForCheck();
  }

  private getEditChangesSummary(): { hasChanges: boolean; lines: string[] } {
    if (!this.editState) {
      return { hasChanges: false, lines: [] };
    }

    const changedTimes = this.editState.days.filter(
      (day) => !day.isCancelled && day.isDirty,
    ).length;
    const cancelledDays = this.editState.days.filter((day) => day.isCancelled).length;
    const addedDays = this.editState.addedDays.length;

    const lines: string[] = [];

    if (changedTimes) {
      lines.push(`• ${changedTimes} day(s) with time changes`);
    }

    if (cancelledDays) {
      lines.push(`• ${cancelledDays} day(s) to cancel`);
    }

    if (addedDays) {
      lines.push(`• ${addedDays} newly added day(s)`);
    }

    return { hasChanges: lines.length > 0, lines };
  }

  private hasUnsavedEditChanges(): boolean {
    return this.getEditChangesSummary().hasChanges;
  }

  private hasTimeZoneDesignator(value: string): boolean {
    return /([zZ]|[+-]\d{2}:?\d{2})$/.test(value);
  }

  private parseApiUtcToLocal(value: string | null | undefined): dayjs.Dayjs {
    const normalized = (value ?? '').trim();
    if (!normalized) {
      return dayjs('');
    }

    const dateTime = this.hasTimeZoneDesignator(normalized)
      ? dayjs(normalized)
      : dayjs.utc(normalized);
    return dateTime.local();
  }

  private roundToQuarterHour(dateTime: dayjs.Dayjs): dayjs.Dayjs {
    if (!dateTime.isValid()) {
      return dateTime;
    }

    const roundedMinutes = Math.round(dateTime.minute() / 15) * 15;
    const base = dateTime.second(0).millisecond(0);

    if (roundedMinutes === 60) {
      return base.add(1, 'hour').minute(0);
    }

    return base.minute(roundedMinutes);
  }

  toDateTimeLocalValue(dateTime: dayjs.Dayjs): string {
    return this.roundToQuarterHour(dateTime.local()).format('YYYY-MM-DDTHH:mm');
  }

  fromDateTimeLocalValue(value: string): dayjs.Dayjs {
    return this.roundToQuarterHour(dayjs(value));
  }

  private apiUtcStringToLocalDate(value: string | null | undefined): Date | null {
    const normalized = (value ?? '').trim();
    if (!normalized) {
      return null;
    }

    const utcDateTime = this.hasTimeZoneDesignator(normalized)
      ? dayjs(normalized)
      : dayjs.utc(normalized);

    return utcDateTime.local().toDate();
  }

  getEarliestSelectableDate(): dayjs.Dayjs {
    return dayjs().startOf('day').add(4, 'day');
  }

  isDateSelectable(date: dayjs.Dayjs): boolean {
    const userRoles = this.accountService.user?.roles ?? [];
    const isAdminUser = userRoles.some((role) => role.name.toLowerCase() === 'admin');
    return isAdminUser || this.isSameOrAfterDay(date, this.getEarliestSelectableDate());
  }

  canCancelStart(start: dayjs.Dayjs): boolean {
    return start.isAfter(dayjs());
  }

  canEditExistingDay(day: EditDayVm): boolean {
    return this.isSameOrAfterDay(day.start, dayjs().startOf('day'));
  }

  canRemoveExistingDay(day: EditDayVm): boolean {
    if (!this.editState || !this.isOpenGroupOwnedByCurrentUser()) {
      return false;
    }

    if (this.editState.status === 'Pending') {
      return true;
    }

    return this.canEditExistingDay(day);
  }

  get canEditOpenGroup(): boolean {
    if (!this.editState) {
      return false;
    }

    const editableStatus =
      this.editState.status === 'Pending' || this.editState.status === 'Approved';

    if (!editableStatus) {
      return false;
    }

    return this.isOpenGroupOwnedByCurrentUser();
  }

  get canCancelOpenGroup(): boolean {
    if (!this.editState || this.isBusy) {
      return false;
    }

    if (this.editState.status === 'Cancelled') {
      return false;
    }

    const isOwnedByCurrentUser = this.isOpenGroupOwnedByCurrentUser();

    if (!isOwnedByCurrentUser && !this.isAdmin) {
      return false;
    }

    if (!isOwnedByCurrentUser && this.editState.status !== 'Approved') {
      return false;
    }

    const hasPastApprovedDays =
      this.editState.status !== 'Pending' &&
      this.editState.days.some(
        (day) => !day.isCancelled && !this.canEditExistingDay(day),
      );

    return !hasPastApprovedDays;
  }

  private isOpenGroupOwnedByCurrentUser(): boolean {
    if (!this.editState) {
      return false;
    }

    if (this.editState.scope === 'mine') {
      return true;
    }

    const currentUserId = Number(
      this.currentUserDetail?.id ?? (this.accountService.user as { id?: number | string | null })?.id,
    );
    const requestEmployeeId = Number(this.editState.employeeId);

    return (
      Number.isFinite(currentUserId) &&
      Number.isFinite(requestEmployeeId) &&
      currentUserId > 0 &&
      currentUserId === requestEmployeeId
    );
  }

  private isDateSelectableForEdit(date: dayjs.Dayjs): boolean {
    return this.isSameOrAfterDay(date.startOf('day'), dayjs().startOf('day'));
  }

  private isSameOrAfterDay(a: dayjs.Dayjs, b: dayjs.Dayjs): boolean {
    return a.isSame(b, 'day') || a.isAfter(b, 'day');
  }

  private isSameOrBeforeDay(a: dayjs.Dayjs, b: dayjs.Dayjs): boolean {
    return a.isSame(b, 'day') || a.isBefore(b, 'day');
  }

  setTab(tab: OfftimeTab): void {
    if (this.activeTab === tab && this.viewMode === 'list') {
      return;
    }

    this.activeTab = tab;
    this.viewMode = 'list';
    this.clearToast();
    this.closeEdit(true);
    this.safeMarkForCheck();

    if (tab !== 'Create') {
      this.loadTab(tab);
    }
  }

  loadTab(tab: OfftimeTab, minePage?: number, allPage?: number): void {
    if (tab === 'Create') {
      return;
    }

    const pages = this.resolveAndSetPagesForTab(tab, minePage, allPage);
    const filter = this.getListFilterForTab(tab);

    this.withBusy(this.loadTabBothScopes(filter, pages.minePage, pages.allPage))
      .pipe(
        tap(({ mine, allOthers }) => {
          this.assignTabResponses(filter, mine, allOthers);
        }),
        catchError((error) => {
          console.error('Failed loading off-time requests:', error);
          this.assignTabResponses(filter, null, null);
          return of(null);
        }),
      )
      .subscribe();
  }

  applyOfftimeFilters(): void {
    if (this.activeTab === 'Create') {
      return;
    }

    this.resetPagesForTab(this.activeTab);
    this.loadTab(this.activeTab);
  }

  onOfftimeOrderByChange(orderBy: OfftimeGroupOrderBy): void {
    if (!this.isValidOfftimeOrderBy(orderBy)) {
      return;
    }

    if (this.offtimeOrderBy === orderBy) {
      return;
    }

    this.offtimeOrderBy = orderBy;
    this.applyOfftimeFilters();
  }

  onOfftimeDateRangeChange(): void {
    this.applyOfftimeFilters();
  }

  clearOfftimeDateRange(): void {
    this.offtimeStartDate = '';
    this.offtimeEndDate = '';
    this.applyOfftimeFilters();
  }

  private getListFilterForTab(tab: Exclude<OfftimeTab, 'Create'>): OfftimeListFilter {
    switch (tab) {
      case 'Pending':
        return this.buildListFilter({ isApproved: null });
      case 'Approved':
        return this.buildListFilter({ isApproved: true });
      case 'Rejected':
        return this.buildListFilter({ isApproved: false });
      case 'Cancelled':
        return this.buildListFilter({ isApproved: null, isCanceled: true });
    }
  }

  private resolveAndSetPagesForTab(
    tab: Exclude<OfftimeTab, 'Create'>,
    minePage?: number,
    allPage?: number,
  ): { minePage: number; allPage: number } {
    const normalize = (value: number | undefined, fallback: number): number =>
      Math.max(1, Number(value ?? fallback) || 1);

    switch (tab) {
      case 'Pending':
        this.pendingMinePage = normalize(minePage, this.pendingMinePage);
        this.pendingAllPage = normalize(allPage, this.pendingAllPage);
        return { minePage: this.pendingMinePage, allPage: this.pendingAllPage };

      case 'Approved':
        this.approvedMinePage = normalize(minePage, this.approvedMinePage);
        this.approvedAllPage = normalize(allPage, this.approvedAllPage);
        return { minePage: this.approvedMinePage, allPage: this.approvedAllPage };

      case 'Rejected':
        this.rejectedMinePage = normalize(minePage, this.rejectedMinePage);
        this.rejectedAllPage = normalize(allPage, this.rejectedAllPage);
        return { minePage: this.rejectedMinePage, allPage: this.rejectedAllPage };

      case 'Cancelled':
        this.cancelledMinePage = normalize(minePage, this.cancelledMinePage);
        this.cancelledAllPage = normalize(allPage, this.cancelledAllPage);
        return { minePage: this.cancelledMinePage, allPage: this.cancelledAllPage };
    }
  }

  private buildListFilter(base: {
    isApproved: ApprovalFilter;
    isCanceled?: boolean;
  }): OfftimeListFilter {
    return {
      ...base,
      orderBy: this.offtimeOrderBy,
      startDate: this.normalizeDateFilter(this.offtimeStartDate),
      endDate: this.normalizeDateFilter(this.offtimeEndDate),
    };
  }

  private normalizeDateFilter(value: string | null | undefined): string | undefined {
    const trimmed = String(value ?? '').trim();

    if (!trimmed) {
      return undefined;
    }

    const parsed = dayjs(trimmed);

    if (!parsed.isValid()) {
      return undefined;
    }

    return parsed.format('YYYY-MM-DD');
  }

  private isValidOfftimeOrderBy(value: string): value is OfftimeGroupOrderBy {
    return this.offtimeOrderByOptions.some((option) => option.value === value);
  }

  private resetPagesForTab(tab: OfftimeTab): void {
    switch (tab) {
      case 'Pending':
        this.pendingMinePage = 1;
        this.pendingAllPage = 1;
        break;
      case 'Approved':
        this.approvedMinePage = 1;
        this.approvedAllPage = 1;
        break;
      case 'Rejected':
        this.rejectedMinePage = 1;
        this.rejectedAllPage = 1;
        break;
      case 'Cancelled':
        this.cancelledMinePage = 1;
        this.cancelledAllPage = 1;
        break;
      case 'Create':
      default:
        break;
    }
  }

  private loadTabBothScopes(
    filter: OfftimeListFilter,
    minePage = 1,
    allPage = 1,
  ): Observable<{
    mine: ApiPagedResponse<OfftimeRequestGroupListDto[]> | null;
    allOthers: ApiPagedResponse<OfftimeRequestGroupListDto[]> | null;
  }> {
    return forkJoin({
      mine: this.loadGroupsForScope('mine', filter, minePage).pipe(
        catchError((error) => {
          console.error('Failed loading my off-time requests:', error);
          return of(null);
        }),
      ),

      allOthers: this.loadGroupsForScope('all', filter, allPage).pipe(
        catchError((error) => {
          console.error('Failed loading all off-time requests:', error);
          return of(null);
        }),
      ),
    });
  }

  private loadGroupsForScope(
    scope: OfftimeGroupScope,
    filter: OfftimeListFilter,
    pageNumber: number,
  ): Observable<ApiPagedResponse<OfftimeRequestGroupListDto[]>> {
    return this.offtimeService
      .getDayOffRequestGroups({
        scope,
        pageNumber,
        pageSize: this.pageSize,
        isApproved: filter.isApproved,
        isCanceled: filter.isCanceled === true ? true : undefined,
        orderBy: filter.orderBy,
        startDate: filter.startDate,
        endDate: filter.endDate,
      })
      .pipe(
        map((response) => ({
          ...response,
          data: (response.data ?? []).map((group: any) => ({
            ...group,
            createdAt: this.apiUtcStringToLocalDate(group.createdAt),
            approvedAt: this.apiUtcStringToLocalDate(group.approvedAt),
          })),
        })),
      );
  }

  private waitForCreatedGroup(groupId: number): Observable<OfftimeRequestGroupDetailsDto> {
    return timer(350).pipe(
      switchMap(() =>
        this.offtimeService.getDayOffRequestGroupById(groupId, {
          scope: 'all',
        }),
      ),
      retry({
        count: 6,
        delay: (_error, retryCount) => timer(400 * retryCount),
      }),
      catchError((error) => {
        console.error('Created request group never became readable for file attach:', error);
        return throwError(() => error);
      }),
    );
  }

  private assignTabResponses(
    filter: OfftimeListFilter,
    mine: ApiPagedResponse<OfftimeRequestGroupListDto[]> | null,
    allOthers: ApiPagedResponse<OfftimeRequestGroupListDto[]> | null,
  ): void {
    if (filter.isCanceled === true) {
      this.cancelledMineResp = mine;
      this.cancelledAllResp = allOthers;
      this.refreshTabCounts();
      return;
    }

    if (filter.isApproved === null) {
      this.pendingMineResp = mine;
      this.pendingAllResp = allOthers;
      this.refreshTabCounts();
      return;
    }

    if (filter.isApproved === true) {
      this.approvedMineResp = mine;
      this.approvedAllResp = allOthers;
      this.refreshTabCounts();
      return;
    }

    this.rejectedMineResp = mine;
    this.rejectedAllResp = allOthers;
    this.refreshTabCounts();
  }

  private refreshTabCounts(): void {
    this.tabCounts = {
      create: 0,
      pending: this.getCombinedCount(this.pendingMineResp, this.pendingAllResp),
      approved: this.getCombinedCount(this.approvedMineResp, this.approvedAllResp),
      rejected: this.getCombinedCount(this.rejectedMineResp, this.rejectedAllResp),
      cancelled: this.getCombinedCount(this.cancelledMineResp, this.cancelledAllResp),
    };

    this.safeMarkForCheck();
  }

  prevPage(tab: Exclude<OfftimeTab, 'Create'>, scope: OfftimeGroupScope): void {
    switch (tab) {
      case 'Pending':
        if (scope === 'mine') {
          this.pendingMinePage = Math.max(1, this.pendingMinePage - 1);
        } else {
          this.pendingAllPage = Math.max(1, this.pendingAllPage - 1);
        }
        break;
      case 'Approved':
        if (scope === 'mine') {
          this.approvedMinePage = Math.max(1, this.approvedMinePage - 1);
        } else {
          this.approvedAllPage = Math.max(1, this.approvedAllPage - 1);
        }
        break;
      case 'Rejected':
        if (scope === 'mine') {
          this.rejectedMinePage = Math.max(1, this.rejectedMinePage - 1);
        } else {
          this.rejectedAllPage = Math.max(1, this.rejectedAllPage - 1);
        }
        break;
      case 'Cancelled':
        if (scope === 'mine') {
          this.cancelledMinePage = Math.max(1, this.cancelledMinePage - 1);
        } else {
          this.cancelledAllPage = Math.max(1, this.cancelledAllPage - 1);
        }
        break;
    }

    this.loadTab(tab);
  }

  nextPage(tab: Exclude<OfftimeTab, 'Create'>, scope: OfftimeGroupScope): void {
    const response = this.getRespFor(tab, scope);
    if (!response) {
      return;
    }

    switch (tab) {
      case 'Pending':
        if (scope === 'mine') {
          this.pendingMinePage = Math.min(response.totalPages ?? 1, this.pendingMinePage + 1);
        } else {
          this.pendingAllPage = Math.min(response.totalPages ?? 1, this.pendingAllPage + 1);
        }
        break;
      case 'Approved':
        if (scope === 'mine') {
          this.approvedMinePage = Math.min(response.totalPages ?? 1, this.approvedMinePage + 1);
        } else {
          this.approvedAllPage = Math.min(response.totalPages ?? 1, this.approvedAllPage + 1);
        }
        break;
      case 'Rejected':
        if (scope === 'mine') {
          this.rejectedMinePage = Math.min(response.totalPages ?? 1, this.rejectedMinePage + 1);
        } else {
          this.rejectedAllPage = Math.min(response.totalPages ?? 1, this.rejectedAllPage + 1);
        }
        break;
      case 'Cancelled':
        if (scope === 'mine') {
          this.cancelledMinePage = Math.min(response.totalPages ?? 1, this.cancelledMinePage + 1);
        } else {
          this.cancelledAllPage = Math.min(response.totalPages ?? 1, this.cancelledAllPage + 1);
        }
        break;
    }

    this.loadTab(tab);
  }

  getRespFor(tab: Exclude<OfftimeTab, 'Create'>, scope: OfftimeGroupScope): ApiPagedResponse<OfftimeRequestGroupListDto[]> | null {
    if (tab === 'Pending') {
      return scope === 'mine' ? this.pendingMineResp : this.pendingAllResp;
    }

    if (tab === 'Approved') {
      return scope === 'mine' ? this.approvedMineResp : this.approvedAllResp;
    }

    if (tab === 'Rejected') {
      return scope === 'mine' ? this.rejectedMineResp : this.rejectedAllResp;
    }

    return scope === 'mine' ? this.cancelledMineResp : this.cancelledAllResp;
  }

  get canApproveReject(): boolean {
    return (
      !!this.editState && this.editState.scope === 'all' && this.editState.status === 'Pending'
    );
  }

  get canAddFilesToOpenGroup(): boolean {
    return this.canEditOpenGroup;
  }

  async approveOpenGroup(): Promise<void> {
    if (!this.editState || !this.canApproveReject) {
      return;
    }

    const ok = await this.openConfirm({
      title: 'Approve request?',
      message: 'Approve this time off request group?\n\nThis will mark the request as approved.',
      okText: 'Approve',
      cancelText: 'Cancel',
      kind: 'default',
    });

    if (!ok) {
      return;
    }

    const groupId = this.editState.groupId;

    this.withBusy(this.offtimeService.approveDayOffRequestGroup(groupId))
      .pipe(
        tap(() => {
          const currentTab = this.activeTab;

          this.closeEdit(true);
          this.snackbarService.success('Request approved.');

          this.loadTab(currentTab === 'Create' ? 'Pending' : currentTab);
          if (currentTab !== 'Approved') {
            this.loadTab('Approved');
          }
        }),
      )
      .subscribe();
  }

  async rejectOpenGroup(): Promise<void> {
    if (!this.editState || !this.canApproveReject) {
      return;
    }

    const ok = await this.openConfirm({
      title: 'Reject request?',
      message: 'Reject this time off request group?\n\nThis will mark the request as rejected.',
      okText: 'Reject',
      cancelText: 'Cancel',
      kind: 'danger',
    });

    if (!ok) {
      return;
    }

    const groupId = this.editState.groupId;

    this.withBusy(this.offtimeService.rejectDayOffRequestGroup(groupId))
      .pipe(
        tap(() => {
          const currentTab = this.activeTab;

          this.closeEdit(true);
          this.snackbarService.success('Request rejected.');

          this.loadTab(currentTab === 'Create' ? 'Pending' : currentTab);
          if (currentTab !== 'Rejected') {
            this.loadTab('Rejected');
          }
        }),
        catchError(() => {
          this.showModalMessage('Failed to reject request', 'error');
          return of(null);
        }),
      )
      .subscribe();
  }

  private clampToMinMonth(month: dayjs.Dayjs): dayjs.Dayjs {
    const minMonth = this.getEarliestSelectableDate().startOf('month');
    return month.isBefore(minMonth, 'month') ? minMonth : month;
  }

  private syncPickerToCalendar(): void {
    this.pickerYear = this.calendarMonth.year();
    this.pickerMonthIndex = this.calendarMonth.month();
  }

  openMonthPicker(): void {
    this.isMonthPickerOpen = true;
    this.syncPickerToCalendar();
    this.safeMarkForCheck();
  }

  closeMonthPicker(): void {
    this.isMonthPickerOpen = false;
    this.safeMarkForCheck();
  }

  getPickerYears(): number[] {
    const currentYear = dayjs().year();
    const endYear = currentYear + this.maxFutureYears;
    const years: number[] = [];

    for (let year = currentYear; year <= endYear; year += 1) {
      years.push(year);
    }

    return years;
  }

  getPickerMonthsForYear(year: number): Array<{ index: number; label: string; disabled: boolean }> {
    const minMonth = this.getEarliestSelectableDate().startOf('month');
    const months: Array<{ index: number; label: string; disabled: boolean }> = [];

    for (let index = 0; index < 12; index += 1) {
      const month = dayjs().year(year).month(index).startOf('month');
      months.push({
        index,
        label: month.format('MMM'),
        disabled: month.isBefore(minMonth, 'month'),
      });
    }

    return months;
  }

  selectPickerYear(year: number): void {
    this.pickerYear = year;

    const minMonth = this.getEarliestSelectableDate().startOf('month');
    const candidate = dayjs().year(year).month(this.pickerMonthIndex).startOf('month');

    if (candidate.isBefore(minMonth, 'month')) {
      this.pickerMonthIndex = minMonth.month();
      this.pickerYear = minMonth.year();
    }

    this.safeMarkForCheck();
  }

  applyPickerMonth(monthIndex: number): void {
    this.calendarMonth = this.clampToMinMonth(
      dayjs().year(this.pickerYear).month(monthIndex).startOf('month'),
    );
    this.rebuildCalendarCells();
    this.isMonthPickerOpen = false;
    this.syncPickerToCalendar();
    this.safeMarkForCheck();
  }

  prevMonth(): void {
    this.calendarMonth = this.clampToMinMonth(
      this.calendarMonth.subtract(1, 'month').startOf('month'),
    );
    this.rebuildCalendarCells();
    this.syncPickerToCalendar();
    this.safeMarkForCheck();
  }

  nextMonth(): void {
    this.calendarMonth = this.calendarMonth.add(1, 'month').startOf('month');
    this.rebuildCalendarCells();
    this.syncPickerToCalendar();
    this.safeMarkForCheck();
  }

  private rebuildCalendarCells(): void {
    const firstOfMonth = this.calendarMonth.startOf('month');
    const startGrid = firstOfMonth.subtract(firstOfMonth.day(), 'day');

    this.calendarCells = Array.from({ length: 42 }, (_, index) => startGrid.add(index, 'day'));
  }

  toggleDaySelection(day: dayjs.Dayjs): void {
    this.clearToast();

    const normalizedDay = day.startOf('day');

    if (!this.isDateSelectable(normalizedDay)) {
      this.snackbarService.error(
        `Time off must be requested at least 3 full days in advance. Earliest selectable date is ${this.getEarliestSelectableDate().format(
          'MM/DD/YYYY',
        )}.`,
      );
      return;
    }

    if (this.selectionMode === 'multi') {
      this.toggleSingleDay(normalizedDay);
      this.safeMarkForCheck();
      return;
    }

    if (!this.rangeStart) {
      this.rangeStart = normalizedDay;
      this.selectedDays = [this.buildSelectedDayVm(normalizedDay)];
      this.safeMarkForCheck();
      return;
    }

    const start = this.rangeStart;
    const end = normalizedDay;
    this.rangeStart = null;

    const from = start.isBefore(end) ? start : end;
    const to = start.isBefore(end) ? end : start;

    const days: SelectedDayVm[] = [];
    let cursor = from.clone();

    while (this.isSameOrBeforeDay(cursor, to)) {
      if (this.isDateSelectable(cursor)) {
        days.push(this.buildSelectedDayVm(cursor));
      }

      cursor = cursor.add(1, 'day');
    }

    this.selectedDays = this.sortSelectedDays(days);
    this.applyDefaultTimesToAllSelectedDays();
    this.safeMarkForCheck();
  }

  private toggleSingleDay(day: dayjs.Dayjs): void {
    const key = day.format('YYYY-MM-DD');
    const existingIndex = this.selectedDays.findIndex(
      (selected) => selected.date.format('YYYY-MM-DD') === key,
    );

    if (existingIndex >= 0) {
      this.selectedDays = this.selectedDays.filter((_, index) => index !== existingIndex);
      return;
    }

    this.selectedDays = this.sortSelectedDays([...this.selectedDays, this.buildSelectedDayVm(day)]);
    this.applyDefaultTimesToAllSelectedDays();
  }

  private buildSelectedDayVm(day: dayjs.Dayjs): SelectedDayVm {
    return {
      date: day.startOf('day'),
      startMinutes: this.defaultStartMinutes,
      endMinutes: this.defaultEndMinutes,
    };
  }

  private sortSelectedDays(days: SelectedDayVm[]): SelectedDayVm[] {
    return [...days].sort((a, b) => a.date.valueOf() - b.date.valueOf());
  }

  clearSelection(): void {
    this.selectedDays = [];
    this.isPerDayEditorOpen = false;
    this.rangeStart = null;
    this.clearToast();
    this.safeMarkForCheck();
  }

  resetCreate(): void {
    this.clearSelection();
    this.createReason = '';
    this.defaultStartMinutes = 8 * 60;
    this.defaultEndMinutes = 17 * 60;
    this.selectedCreateFiles = [];
    this.safeMarkForCheck();
  }

  private getSelectedDayHours(day: SelectedDayVm): number {
    const startMinutes = this.isPerDayEditorOpen ? day.startMinutes : this.defaultStartMinutes;
    const endMinutes = this.isPerDayEditorOpen ? day.endMinutes : this.defaultEndMinutes;
    return Math.max(0, endMinutes - startMinutes) / 60;
  }

  togglePerDayEditor(): void {
    this.isPerDayEditorOpen = !this.isPerDayEditorOpen;

    if (this.isPerDayEditorOpen) {
      this.applyDefaultTimesToAllSelectedDays();
    }

    this.safeMarkForCheck();
  }

  applyDefaultTimesToAllSelectedDays(): void {
    this.defaultEndMinutes = this.normalizeEndMinutes(
      this.defaultStartMinutes,
      this.defaultEndMinutes,
    );

    this.selectedDays = this.selectedDays.map((day) => ({
      ...day,
      startMinutes: this.defaultStartMinutes,
      endMinutes: this.defaultEndMinutes,
    }));
  }

  getEndTimeOptionsAfter(startMinutes: number | string | null | undefined): TimeOptionVm[] {
    const start = Number(startMinutes);

    if (!Number.isFinite(start)) {
      return this.timeOptions;
    }

    return this.timeOptions.filter((option) => option.minutes > start);
  }

  private getNextAllowedEndMinutes(startMinutes: number): number {
    const next = this.timeOptions.find((option) => option.minutes > startMinutes);
    return next?.minutes ?? startMinutes;
  }

  private normalizeEndMinutes(startMinutes: number, endMinutes: number): number {
    return endMinutes > startMinutes ? endMinutes : this.getNextAllowedEndMinutes(startMinutes);
  }

  onDefaultStartMinutesChange(value: number | string): void {
    this.defaultStartMinutes = Number(value);
    this.defaultEndMinutes = this.normalizeEndMinutes(
      this.defaultStartMinutes,
      this.defaultEndMinutes,
    );

    this.applyDefaultTimesToAllSelectedDays();
    this.safeMarkForCheck();
  }

  onDefaultEndMinutesChange(value: number | string): void {
    this.defaultEndMinutes = Number(value);
    this.applyDefaultTimesToAllSelectedDays();
    this.safeMarkForCheck();
  }

  onSelectedDayStartMinutesChange(day: SelectedDayVm, value: number | string): void {
    day.startMinutes = Number(value);
    day.endMinutes = this.normalizeEndMinutes(day.startMinutes, day.endMinutes);
    this.safeMarkForCheck();
  }

  onSelectedDayEndMinutesChange(day: SelectedDayVm, value: number | string): void {
    day.endMinutes = Number(value);
    this.safeMarkForCheck();
  }

  onEditAddStartMinutesChange(value: number | string): void {
    this.editAddStartMinutes = Number(value);
    this.editAddEndMinutes = this.normalizeEndMinutes(
      this.editAddStartMinutes,
      this.editAddEndMinutes,
    );
    this.safeMarkForCheck();
  }

  onEditAddEndMinutesChange(value: number | string): void {
    this.editAddEndMinutes = Number(value);
    this.safeMarkForCheck();
  }

  onCreateFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = Array.from(input?.files ?? []);

    if (!files.length) {
      return;
    }

    const existingKeys = new Set(
      this.selectedCreateFiles.map((file) => `${file.name}|${file.size}|${file.lastModified}`),
    );

    const nextFiles = files.filter(
      (file) => !existingKeys.has(`${file.name}|${file.size}|${file.lastModified}`),
    );

    this.selectedCreateFiles = [...this.selectedCreateFiles, ...nextFiles];

    if (input) {
      input.value = '';
    }

    this.safeMarkForCheck();
  }

  removeCreateFile(index: number): void {
    this.selectedCreateFiles = this.selectedCreateFiles.filter((_, i) => i !== index);
    this.safeMarkForCheck();
  }

  submitCreate(): void {
    this.clearToast();

    if (!this.selectedPayCodeId) {
      this.snackbarService.error('Please select a pay code.');
      return;
    }

    if (!this.selectedDays.length) {
      this.snackbarService.error('Please select at least one day.');
      return;
    }

    if (!this.createReason.trim()) {
      this.snackbarService.error('Please enter a reason.');
      return;
    }

    const timeZoneId = this.offtimeService.getLocalTimeZoneId();
    const conflicts: string[] = [];

    const dayOffRequests = this.selectedDays.map((day) => {
      const startMinutes = this.isPerDayEditorOpen ? day.startMinutes : this.defaultStartMinutes;
      const endMinutes = this.isPerDayEditorOpen ? day.endMinutes : this.defaultEndMinutes;

      const start = this.buildLocalDateTime(day.date, startMinutes);
      const end = this.buildLocalDateTime(day.date, endMinutes);

      if (!end.isAfter(start)) {
        conflicts.push(`${day.date.format('MM/DD/YYYY')}: end time must be after start time`);
      }

      return {
        startTime: this.formatForApi(start),
        endTime: this.formatForApi(end),
        timeZoneId,
      };
    });

    if (conflicts.length) {
      this.snackbarService.error(this.renderConflictMessage(conflicts));
      return;
    }

    const createBalanceImpact = this.createBalanceImpact;

    if (
      createBalanceImpact &&
      !createBalanceImpact.allowNegativeBalance &&
      createBalanceImpact.currentBalance - createBalanceImpact.requestedHours < 0
    ) {
      this.snackbarService.error(
        `${createBalanceImpact.leaveType} request exceeds available balance. Current balance: ${createBalanceImpact.currentBalance.toFixed(2)}h, Requested: ${createBalanceImpact.requestedHours.toFixed(2)}h.`,
      );
      return;
    }

    const body: CreateDayOffRequestBody = {
      employeeId: this.isAdmin ? this.createEmployeeId : null,
      payCodeId: this.selectedPayCodeId,
      reason: this.createReason.trim(),
      dayOffRequests,
    };

    const createFiles = [...this.selectedCreateFiles];

    this.withBusy(this.offtimeService.createDayOffRequestGroup(body))
      .pipe(
        switchMap((createdGroupId) => {
          const groupId = Number((createdGroupId as number | null | undefined) ?? 0);

          if (!groupId) {
            throw new Error('Create succeeded but no request group id was returned.');
          }

          if (!createFiles.length) {
            return of({ uploaded: false, groupId });
          }

          return this.waitForCreatedGroup(groupId).pipe(
            switchMap(() => this.offtimeService.addFilesToDayOffRequestGroup(groupId, createFiles)),
            map(() => ({ uploaded: true, groupId })),
            catchError((error) => {
              console.error('Failed attaching files after create:', error);
              return of({ uploaded: false, groupId });
            }),
          );
        }),
        tap(({ uploaded }) => {
          this.snackbarService.success(
            uploaded
              ? 'Time off request submitted with attachments.'
              : 'Time off request submitted.',
          );

          this.resetCreate();
          this.activeTab = 'Pending';
          this.loadTab('Pending');
          this.safeMarkForCheck();
        }),
        catchError((error) => {
          console.error('Failed creating time off request:', error);
          this.snackbarService.error('Failed to submit time off request.');
          return of(null);
        }),
      )
      .subscribe();
  }

  getBalanceClass(value: number): 'positive' | 'negative' | 'neutral' {
    if (value > 0) return 'positive';
    if (value < 0) return 'negative';
    return 'neutral';
  }

  getRequestBalanceImpactForGroup(
    group: OfftimeRequestGroupListDto | OfftimeRequestGroupDetailsDto,
  ): BalanceImpactVm | null {
    const leaveType = this.getLeaveTypeFromPayCodeName(group.payCode?.name ?? null);
    const employee = this.users.find((u) => u.id === group.employee?.id) ?? null;

    if (!leaveType || !employee) {
      return null;
    }

    const currentBalance = this.getAvailableHoursFromBalances(
      employee.employeeLeaveBalances,
      leaveType,
    );
    const requestedHours = this.sumRequestDateHours(group.dayOffRequestDates ?? []);
    const allowNegativeBalance = this.getAllowNegativeBalanceForEmployee(employee, leaveType);

    return {
      leaveType,
      currentBalance,
      requestedHours,
      allowNegativeBalance,
    };
  }

  getEditBalanceImpact(): BalanceImpactVm | null {
    const employeeId = this.editState?.employeeId ?? null;
    if (employeeId === null) {
      return null;
    }

    const employee = this.users.find((u) => u.id === employeeId) ?? null;
    const payCodeId = this.editState?.payCodeId ?? null;
    const payCode = this.payCodes.find((pc) => pc.id === payCodeId) ?? null;
    const leaveType = this.getLeaveTypeFromPayCodeName(payCode?.name ?? null);

    if (!employee || !leaveType || !this.editState) {
      return null;
    }

    const existingRequestedHours = this.editState.days
      .filter((day) => !day.isCancelled)
      .reduce((sum, day) => sum + Math.max(0, day.end.diff(day.start, 'minute')) / 60, 0);

    const addedRequestedHours = this.editState.addedDays.reduce(
      (sum, day) => sum + Math.max(0, day.end.diff(day.start, 'minute')) / 60,
      0,
    );

    const requestedHours = existingRequestedHours + addedRequestedHours;
    const currentBalance = this.getAvailableHoursFromBalances(
      employee.employeeLeaveBalances,
      leaveType,
    );
    const allowNegativeBalance = this.getAllowNegativeBalanceForEmployee(employee, leaveType);

    return {
      leaveType,
      currentBalance,
      requestedHours,
      allowNegativeBalance,
    };
  }

  get editRequestedByDisplayName(): string {
    const group = this.getOpenGroupListItem();
    return group ? this.getRequestedByDisplay(group) : this.getCurrentUserDisplayName();
  }

  get editRequestedForDisplayName(): string {
    const group = this.getOpenGroupListItem();
    const employee = group?.employee ?? null;

    if (employee) {
      const first = String(employee.firstName ?? '').trim();
      const last = String(employee.lastName ?? '').trim();
      const fullName = `${first} ${last}`.trim();
      return fullName || '—';
    }

    return this.editEmployeeDisplayName;
  }

  get editRequestedForEmployeeNumber(): string | null {
    const group = this.getOpenGroupListItem();
    return group?.employee?.employeeNumber ?? this.editEmployeeNumber;
  }

  getRequestedByDisplay(group: OfftimeRequestGroupListDto | OfftimeRequestGroupDetailsDto): string {
    const raw = group as any;
    const requestedBy = raw.requestedBy ?? raw.createdBy ?? raw.createdByUser ?? raw.createdUser ?? null;

    if (requestedBy) {
      const first = String(requestedBy.firstName ?? '').trim();
      const last = String(requestedBy.lastName ?? '').trim();
      const fullName = `${first} ${last}`.trim();
      const employeeNumber = requestedBy.employeeNumber ? ` (#${requestedBy.employeeNumber})` : '';

      if (fullName) {
        return `${fullName}${employeeNumber}`;
      }
    }

    return this.getCurrentUserDisplayName();
  }

  getRequestedForDisplay(group: OfftimeRequestGroupListDto | OfftimeRequestGroupDetailsDto): string {
    const employee = group.employee;
    const first = String(employee?.firstName ?? '').trim();
    const last = String(employee?.lastName ?? '').trim();
    const fullName = `${first} ${last}`.trim();
    const employeeNumber = employee?.employeeNumber ? ` (#${employee.employeeNumber})` : '';

    return fullName ? `${fullName}${employeeNumber}` : '—';
  }

  private getCurrentUserDisplayName(): string {
    const first = this.accountService.user?.firstName ?? this.currentUserDetail?.firstName ?? '';
    const last = this.accountService.user?.lastName ?? this.currentUserDetail?.lastName ?? '';
    const fullName = `${first} ${last}`.trim();
    const employeeNumber = this.currentUserDetail?.employeeNumber ? ` (#${this.currentUserDetail.employeeNumber})` : '';

    return fullName ? `${fullName}${employeeNumber}` : '—';
  }

  private getOpenGroupListItem(): OfftimeRequestGroupListDto | undefined {
    if (!this.editState) {
      return undefined;
    }

    return this.findGroupListItem(this.activeTab, this.editState.scope, this.editState.groupId);
  }

  private findGroupListItem(
    tab: OfftimeTab,
    scope: OfftimeGroupScope,
    groupId: number,
  ): OfftimeRequestGroupListDto | undefined {
    if (tab === 'Create') {
      return undefined;
    }

    return this.getRespFor(tab, scope)?.data?.find((group) => group.id === groupId);
  }

  get editEmployeeDisplayName(): string {
    if (!this.editState) {
      return '—';
    }

    if (this.editState.scope === 'mine') {
      const first = this.currentUserDetail?.firstName ?? '';
      const last = this.currentUserDetail?.lastName ?? '';
      const fullName = `${first} ${last}`.trim();
      return fullName || '—';
    }

    const employeeId = this.editState.employeeId;
    if (employeeId == null) {
      return '—';
    }

    const employee = this.users.find((u) => u.id === employeeId);
    if (!employee) {
      return '—';
    }

    return `${employee.firstName} ${employee.lastName}`.trim();
  }

  get editEmployeeNumber(): string | null {
    if (!this.editState) {
      return null;
    }

    if (this.editState.scope === 'mine') {
      return this.currentUserDetail?.employeeNumber ?? null;
    }

    const employeeId = this.editState.employeeId;
    if (employeeId == null) {
      return null;
    }

    return this.users.find((u) => u.id === employeeId)?.employeeNumber ?? null;
  }

  get editCreatedAt(): Date | null {
    if (!this.editState) {
      return null;
    }

    const groupId = this.editState.groupId;
    const scope = this.editState.scope;

    let group: OfftimeRequestGroupListDto | undefined;

    switch (this.activeTab) {
      case 'Pending':
        group =
          scope === 'mine'
            ? this.pendingMineResp?.data?.find((g) => g.id === groupId)
            : this.pendingAllResp?.data?.find((g) => g.id === groupId);
        break;

      case 'Approved':
        group =
          scope === 'mine'
            ? this.approvedMineResp?.data?.find((g) => g.id === groupId)
            : this.approvedAllResp?.data?.find((g) => g.id === groupId);
        break;

      case 'Rejected':
        group =
          scope === 'mine'
            ? this.rejectedMineResp?.data?.find((g) => g.id === groupId)
            : this.rejectedAllResp?.data?.find((g) => g.id === groupId);
        break;

      case 'Cancelled':
        group =
          scope === 'mine'
            ? this.cancelledMineResp?.data?.find((g) => g.id === groupId)
            : this.cancelledAllResp?.data?.find((g) => g.id === groupId);
        break;

      default:
        group = undefined;
        break;
    }

    return (group?.createdAt as Date | null | undefined) ?? null;
  }

  private getLeaveTypeFromPayCodeName(payCodeName: string | null): LeaveType | null {
    const normalized = (payCodeName ?? '').trim().toLowerCase();
    if (normalized === 'vacation') return 'Vacation';
    if (normalized === 'sick') return 'Sick';
    return null;
  }

  private getAvailableHoursFromBalances(
    balances: EmployeeLeaveBalanceDto[] | null | undefined,
    leaveType: LeaveType,
  ): number {
    return balances?.find((b) => b.leaveType === leaveType)?.availableHours ?? 0;
  }

  private getAllowNegativeBalanceForEmployee(
    employee: OfftimeUserListDto | CurrentUserDto,
    leaveType: LeaveType,
  ): boolean {
    if (leaveType === 'Vacation') {
      return !!employee.vacationLeavePolicy?.leavePolicy?.allowNegativeBalance;
    }

    return !!employee.sickLeavePolicy?.leavePolicy?.allowNegativeBalance;
  }

  private sumRequestDateHours(days: Array<{ totalHours: number }>): number {
    return days.reduce((sum, day) => sum + Number(day.totalHours ?? 0), 0);
  }

  private renderConflictMessage(conflicts: string[]): string {
    const uniqueConflicts = Array.from(new Set(conflicts));
    const visibleConflicts = uniqueConflicts.slice(0, 8);
    const moreText =
      uniqueConflicts.length > visibleConflicts.length
        ? ` (+${uniqueConflicts.length - visibleConflicts.length} more)`
        : '';

    return `Cannot save request:\n- ${visibleConflicts.join('\n- ')}${moreText}`;
  }

  openGroup(
    groupId: number,
    expectedStatus: ApprovalFilter,
    isCanceled = false,
    scope: OfftimeGroupScope = 'mine',
  ): void {
    this.clearToast();
    this.clearModalMessage();
    this.viewMode = 'details';

    this.isEditOpen = true;
    this.editState = null;
    this.safeMarkForCheck();

    this.withBusy(
      this.offtimeService.getDayOffRequestGroupById(groupId, {
        scope,
        isApproved: expectedStatus,
        isCanceled: isCanceled ? true : undefined,
      }),
    )
      .pipe(
        tap((dto) => this.initEditStateFromDetails(dto, scope)),
        catchError(() => {
          this.isEditOpen = false;
          this.viewMode = 'list';
          this.safeMarkForCheck();
          return of(null);
        }),
      )
      .subscribe();
  }

  closeEdit(force = false): void {
    if (!force && this.isEditOpen && this.hasUnsavedEditChanges()) {
      void this.openConfirm({
        title: 'Discard changes?',
        message: 'You have unsaved changes.\n\nAre you sure you want to close without saving?',
        okText: 'Discard',
        cancelText: 'Keep editing',
        kind: 'danger',
      }).then((ok) => {
        if (!ok) {
          return;
        }

        this.isEditOpen = false;
        this.editState = null;
        this.selectedAttachmentFiles = [];
        this.clearModalMessage();
        this.safeMarkForCheck();
      });

      return;
    }

    this.isEditOpen = false;
    this.editState = null;
    this.selectedAttachmentFiles = [];
    this.clearModalMessage();
    this.safeMarkForCheck();
  }

  backToRequestList(): void {
    this.viewMode = 'list';
    this.isEditOpen = false;
    this.editState = null;
    this.selectedAttachmentFiles = [];
    this.clearModalMessage();
    this.safeMarkForCheck();

    if (this.activeTab !== 'Create') {
      this.loadTab(this.activeTab);
    }
  }

  private initEditStateFromDetails(
    group: OfftimeRequestGroupDetailsDto,
    scope: OfftimeGroupScope,
  ): void {
    const timeZoneId = this.offtimeService.getLocalTimeZoneId();

    const status: RequestStatus =
      (group as any).isCanceled === true
        ? 'Cancelled'
        : group.isApproved === null
          ? 'Pending'
          : group.isApproved === true
            ? 'Approved'
            : 'Rejected';

    this.editState = {
      groupId: group.id,
      employeeId: group.employee?.id ?? null,
      payCodeId: group.payCode?.id,
      reason: group.reason ?? '',
      status,
      scope,
      days: (group.dayOffRequestDates ?? []).map((day) => ({
        id: day.id,
        start: this.roundToQuarterHour(this.parseApiUtcToLocal(day.startTime)),
        end: this.roundToQuarterHour(this.parseApiUtcToLocal(day.endTime)),
        timeZoneId: day.timeZoneId || timeZoneId,
        isDirty: false,
        isCancelled: false,
      })),
      addedDays: [],
      files: (group.files ?? []).map((file) => ({
        id: file.id,
        url: file.url,
        fileName: file.fileName ?? null,
      })),
    };

    this.selectedAttachmentFiles = [];
    this.safeMarkForCheck();
  }

  onAttachmentFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const files = Array.from(input?.files ?? []);

    if (!files.length) {
      return;
    }

    const existingKeys = new Set(
      this.selectedAttachmentFiles.map((file) => `${file.name}|${file.size}|${file.lastModified}`),
    );

    const nextFiles = files.filter(
      (file) => !existingKeys.has(`${file.name}|${file.size}|${file.lastModified}`),
    );

    this.selectedAttachmentFiles = [...this.selectedAttachmentFiles, ...nextFiles];

    if (input) {
      input.value = '';
    }

    this.safeMarkForCheck();
  }

  removeAttachmentFile(index: number): void {
    this.selectedAttachmentFiles = this.selectedAttachmentFiles.filter((_, i) => i !== index);
    this.safeMarkForCheck();
  }

  uploadFilesForOpenGroup(): void {
    if (!this.editState || !this.canAddFilesToOpenGroup) {
      return;
    }

    if (!this.selectedAttachmentFiles.length) {
      this.showModalMessage('Please choose at least one file.', 'error', 2500);
      return;
    }

    this.withBusy(
      this.offtimeService.addFilesToDayOffRequestGroup(
        this.editState.groupId,
        this.selectedAttachmentFiles,
      ),
    )
      .pipe(
        switchMap(() =>
          this.offtimeService.getDayOffRequestGroupById(this.editState!.groupId, {
            scope: this.editState!.scope,
            isApproved:
              this.editState!.status === 'Cancelled'
                ? null
                : this.editState!.status === 'Pending'
                  ? null
                  : this.editState!.status === 'Approved',
            isCanceled: this.editState!.status === 'Cancelled' ? true : undefined,
          }),
        ),
        tap((dto) => {
          if (this.editState) {
            this.editState.files = (dto.files ?? []).map((file) => ({
              id: file.id,
              url: file.url,
              fileName: file.fileName ?? null,
            }));
          }

          this.selectedAttachmentFiles = [];
          this.showModalMessage('Files uploaded successfully.', 'success', 3000);
          this.safeMarkForCheck();
        }),
        catchError(() => {
          this.showModalMessage('Failed to upload files.', 'error');
          return of(null);
        }),
      )
      .subscribe();
  }

  onEditStartChange(day: EditDayVm, value: string): void {
    day.start = this.fromDateTimeLocalValue(value);
    day.isDirty = true;
    this.safeMarkForCheck();
  }

  onEditEndChange(day: EditDayVm, value: string): void {
    day.end = this.fromDateTimeLocalValue(value);
    day.isDirty = true;
    this.safeMarkForCheck();
  }

  async cancelExistingDay(dayId: number): Promise<void> {
    if (!this.editState) {
      return;
    }

    const day = this.editState.days.find((item) => item.id === dayId);
    if (!day) {
      return;
    }

    if (!this.canRemoveExistingDay(day)) {
      this.showModalMessage(
        !this.isOpenGroupOwnedByCurrentUser()
          ? 'You can only remove individual days from your own requests.'
          : this.editState.status === 'Pending'
            ? 'Pending requests can be removed even when the requested day has passed.'
            : 'You can only remove approved time off for today or a future day.',
        'error',
      );
      return;
    }

    const ok = await this.openConfirm({
      title: 'Remove day?',
      message: `Are you sure you want to remove this day?\n\n${day.start.format(
        'M/D/YYYY h:mm A',
      )} – ${day.end.format('h:mm A')}`,
      okText: 'Remove',
      cancelText: 'Keep',
      kind: 'danger',
    });

    if (!ok) {
      return;
    }

    this.editState.days = this.editState.days.map((item) =>
      item.id === dayId ? { ...item, isCancelled: true, isDirty: false } : item,
    );

    this.safeMarkForCheck();
  }

  undoCancelExistingDay(dayId: number): void {
    if (!this.editState) {
      return;
    }

    this.editState.days = this.editState.days.map((day) =>
      day.id === dayId ? { ...day, isCancelled: false } : day,
    );

    this.safeMarkForCheck();
  }

  async cancelEntireGroup(): Promise<void> {
    if (!this.editState) {
      return;
    }

    const isOwnedByCurrentUser = this.isOpenGroupOwnedByCurrentUser();

    if (!isOwnedByCurrentUser && !this.isAdmin) {
      this.showModalMessage('You can only cancel your own requests here.', 'error', 3500);
      return;
    }

    if (!isOwnedByCurrentUser && this.editState.status !== 'Approved') {
      this.showModalMessage('Only approved requests from other employees can be cancelled.', 'error', 3500);
      return;
    }

    const hasPastApprovedDays =
      this.editState.status !== 'Pending' &&
      this.editState.days.some(
        (day) => !day.isCancelled && !this.canEditExistingDay(day),
      );

    if (hasPastApprovedDays) {
      this.showModalMessage(
        'This approved request includes past time off and cannot be cancelled.',
        'error',
      );
      return;
    }

    const ok = await this.openConfirm({
      title: 'Cancel request?',
      message: isOwnedByCurrentUser
        ? 'Are you sure you want to cancel the entire time off request?\n\nThis will cancel all future days in this request.'
        : 'Are you sure you want to cancel this approved time off request?\n\nNo dates or times will be changed. The request will be cancelled.',
      okText: 'Cancel request',
      cancelText: 'Keep request',
      kind: 'danger',
    });

    if (!ok) {
      return;
    }

    this.withBusy(this.offtimeService.cancelDayOffRequestGroup(this.editState.groupId))
      .pipe(
        tap(() => {
          const currentTab = this.activeTab;

          this.closeEdit(true);
          this.snackbarService.success('Request cancelled.');

          if (currentTab !== 'Create') {
            this.loadTab(currentTab);
          }

          if (currentTab !== 'Cancelled') {
            this.loadTab('Cancelled');
          }
        }),
        catchError(() => {
          this.showModalMessage('Failed to cancel request.', 'error');
          return of(null);
        }),
      )
      .subscribe();
  }

  addNewDayToEditFromInputs(dateStr: string, startMinutes: number, endMinutes: number): void {
    if (!dateStr) {
      this.showModalMessage('Pick a date first.', 'error', 2500);
      return;
    }

    this.addNewDayToEdit(dayjs(dateStr), startMinutes, endMinutes);
  }

  addNewDayToEdit(date: dayjs.Dayjs, startMinutes: number, endMinutes: number): void {
    if (!this.editState) {
      return;
    }

    if (!this.canEditOpenGroup) {
      this.showModalMessage(
        'Only your own pending or approved requests can be edited.',
        'error',
        3500,
      );
      return;
    }

    const day = date.startOf('day');

    if (!this.isDateSelectableForEdit(day)) {
      this.showModalMessage('You can only add today or a future day to an existing request.', 'error', 4500);
      return;
    }

    const start = this.buildLocalDateTime(day, startMinutes);
    const end = this.buildLocalDateTime(day, endMinutes);

    if (!end.isAfter(start)) {
      this.showModalMessage('End time must be after start time.', 'error');
      return;
    }


    const timeZoneId = this.offtimeService.getLocalTimeZoneId();
    const clientId = this.makeClientId();

    this.editState.addedDays = [
      ...this.editState.addedDays,
      {
        clientId,
        start,
        end,
        timeZoneId,
        isNew: true,
      },
    ];

    this.editAddDate = '';
    this.editAddStartMinutes = 8 * 60;
    this.editAddEndMinutes = 17 * 60;

    this.safeMarkForCheck();
  }

  removeAddedDay(clientId: string): void {
    if (!this.editState) {
      return;
    }

    this.editState.addedDays = this.editState.addedDays.filter((day) => day.clientId !== clientId);
    this.safeMarkForCheck();
  }

  async saveEdits(): Promise<void> {
    if (!this.editState) {
      return;
    }

    if (!this.canEditOpenGroup) {
      this.showModalMessage(
        'Only your own pending or approved requests can be edited.',
        'error',
        3500,
      );
      return;
    }

    this.clearModalMessage();

    const summary = this.getEditChangesSummary();
    if (!summary.hasChanges) {
      this.showModalMessage('No changes to save.', 'info', 2500);
      return;
    }

    const ok = await this.openConfirm({
      title: 'Save changes?',
      message: `Are you sure you want to save these changes?\n\n${summary.lines.join('\n')}`,
      okText: 'Save',
      cancelText: 'Cancel',
      kind: 'default',
    });

    if (!ok) {
      return;
    }

    const groupId = this.editState.groupId;
    const conflicts: string[] = [];

    const activeExistingDays = this.editState.days.filter((day) => !day.isCancelled);

    for (const day of activeExistingDays) {
      if (!day.end.isAfter(day.start)) {
        conflicts.push(`${day.start.format('MM/DD/YYYY')}: end time must be after start time`);
      }

      if (!this.canEditExistingDay(day) && day.isDirty) {
        conflicts.push(
          `${day.start.format('MM/DD/YYYY')}: cannot change a day before today`,
        );
      }
    }

    for (const addedDay of this.editState.addedDays) {
      const day = addedDay.start.startOf('day');

      if (!this.isDateSelectableForEdit(day)) {
        conflicts.push(`${day.format('MM/DD/YYYY')}: must be today or a future day`);
      }

      if (!addedDay.end.isAfter(addedDay.start)) {
        conflicts.push(`${day.format('MM/DD/YYYY')}: end time must be after start time`);
      }
    }

    if (conflicts.length) {
      this.showModalMessage(this.renderConflictMessage(conflicts), 'error', 7000);
      return;
    }

    const cancelCalls = this.editState.days
      .filter((day) => day.isCancelled)
      .filter((day) => this.canRemoveExistingDay(day))
      .map((day) => this.offtimeService.cancelDayOffRequestDay(day.id));

    const updateCalls = this.editState.days
      .filter((day) => !day.isCancelled && day.isDirty)
      .filter((day) => this.canEditExistingDay(day))
      .map((day) => {
        const body: PatchDayOffRequestBody = {
          startTime: this.formatForApi(day.start),
          endTime: this.formatForApi(day.end),
        };

        return this.offtimeService.patchDayOffRequestDay(day.id, body);
      });

    const addCall =
      this.editState.addedDays.length > 0
        ? this.offtimeService.addMoreRequestsToGroup(
          groupId,
          this.buildAddMoreBody(this.editState.addedDays),
        )
        : null;

    const calls: Observable<unknown>[] = [
      ...cancelCalls,
      ...updateCalls,
      ...(addCall ? [addCall] : []),
    ];

    if (!calls.length) {
      this.showModalMessage('No changes to save.', 'info', 2500);
      return;
    }

    this.withBusy(forkJoin(calls))
      .pipe(
        tap(() => {
          const currentTab = this.activeTab;

          this.closeEdit(true);
          this.snackbarService.success('Changes saved.');
          if (currentTab !== 'Create') {
            this.loadTab(currentTab);
          }
        }),
        catchError(() => {
          this.snackbarService.error('Failed to save changes');
          return of(null);
        }),
      )
      .subscribe();
  }

  private buildAddMoreBody(addedDays: AddedDayVm[]): AddMoreDayOffRequestsBody {
    return addedDays.map((day) => ({
      startTime: this.formatForApi(day.start),
      endTime: this.formatForApi(day.end),
      timeZoneId: day.timeZoneId,
    }));
  }

  private buildLocalDateTime(date: dayjs.Dayjs, minutesFromMidnight: number): dayjs.Dayjs {
    const hours = Math.floor(minutesFromMidnight / 60);
    const minutes = minutesFromMidnight % 60;

    return date.startOf('day').hour(hours).minute(minutes).second(0).millisecond(0);
  }

  private formatForApi(dateTime: dayjs.Dayjs): string {
    return dateTime.format('YYYY-MM-DDTHH:mm:ssZ');
  }

  private buildTimeOptions(): TimeOptionVm[] {
    const options: TimeOptionVm[] = [];

    for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
      options.push({
        minutes,
        label: dayjs().startOf('day').add(minutes, 'minute').format('h:mm A'),
      });
    }

    return options;
  }

  private makeClientId(): string {
    const cryptoApi = (globalThis as { crypto?: Crypto & { randomUUID?: () => string } }).crypto;

    if (cryptoApi?.randomUUID) {
      return cryptoApi.randomUUID();
    }

    return `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;
  }

  minutesFromMidnight(dateTime: dayjs.Dayjs): number {
    if (!dateTime || !dateTime.isValid()) {
      return 0;
    }

    return dateTime.hour() * 60 + dateTime.minute();
  }

  private setTimeOnSameDay(dateTime: dayjs.Dayjs, minutesFromMidnight: number): dayjs.Dayjs {
    const hours = Math.floor(minutesFromMidnight / 60);
    const minutes = minutesFromMidnight % 60;

    return dateTime.startOf('day').hour(hours).minute(minutes).second(0).millisecond(0);
  }

  onEditStartMinutesChange(day: EditDayVm, minutes: number | string): void {
    const startMinutes = Number(minutes);
    const currentEndMinutes = this.minutesFromMidnight(day.end);
    const nextEndMinutes = this.normalizeEndMinutes(startMinutes, currentEndMinutes);

    const nextStart = this.setTimeOnSameDay(day.start, startMinutes);
    const nextEnd = this.setTimeOnSameDay(day.end, nextEndMinutes);

    if (nextStart.isSame(day.start) && nextEnd.isSame(day.end)) {
      return;
    }

    day.start = nextStart;
    day.end = nextEnd;
    day.isDirty = true;
    this.safeMarkForCheck();
  }

  onEditEndMinutesChange(day: EditDayVm, minutes: number | string): void {
    const next = this.setTimeOnSameDay(day.end, Number(minutes));

    if (next.isSame(day.end)) {
      return;
    }

    day.end = next;
    day.isDirty = true;
    this.safeMarkForCheck();
  }

  formatDayLabel(day: dayjs.Dayjs): string {
    return day.format('ddd, MM/DD/YYYY');
  }

  formatMinutesLabel(minutes: number): string {
    return dayjs().startOf('day').add(minutes, 'minute').format('h:mm A');
  }

  isSelectedDay(day: dayjs.Dayjs): boolean {
    const key = day.format('YYYY-MM-DD');
    return this.selectedDays.some((selected) => selected.date.format('YYYY-MM-DD') === key);
  }

  getSelectedDayVm(key: string): SelectedDayVm | null {
    return this.selectedDays.find((day) => day.date.format('YYYY-MM-DD') === key) ?? null;
  }

  private openConfirm(opts: {
    title: string;
    message: string;
    okText?: string;
    cancelText?: string;
    kind?: 'default' | 'danger';
  }): Promise<boolean> {
    if (this.confirmResolver) {
      this.confirmResolver(false);
      this.confirmResolver = null;
    }

    this.confirmTitle = opts.title;
    this.confirmMessage = opts.message;
    this.confirmOkText = opts.okText ?? 'Yes';
    this.confirmCancelText = opts.cancelText ?? 'No';
    this.confirmKind = opts.kind ?? 'default';
    this.confirmOpen = true;

    this.safeMarkForCheck();

    return new Promise<boolean>((resolve) => {
      this.confirmResolver = resolve;
    });
  }

  onConfirmOk(): void {
    this.confirmOpen = false;

    const resolver = this.confirmResolver;
    this.confirmResolver = null;

    this.safeMarkForCheck();
    resolver?.(true);
  }

  onConfirmCancel(): void {
    this.confirmOpen = false;

    const resolver = this.confirmResolver;
    this.confirmResolver = null;

    this.safeMarkForCheck();
    resolver?.(false);
  }

  getTabCount(tab: OfftimeTab): number {
    switch (tab) {
      case 'Pending':
        return this.getCombinedCount(this.pendingMineResp, this.pendingAllResp);
      case 'Approved':
        return this.getCombinedCount(this.approvedMineResp, this.approvedAllResp);
      case 'Rejected':
        return this.getCombinedCount(this.rejectedMineResp, this.rejectedAllResp);
      case 'Cancelled':
        return this.getCombinedCount(this.cancelledMineResp, this.cancelledAllResp);
      case 'Create':
      default:
        return 0;
    }
  }

  private getCombinedCount(
    mine: ApiPagedResponse<OfftimeRequestGroupListDto[]> | null,
    all: ApiPagedResponse<OfftimeRequestGroupListDto[]> | null,
  ): number {
    return Number(mine?.totalCount ?? 0) + Number(all?.totalCount ?? 0);
  }

  getActiveTabCount(): number {
    return this.getTabCount(this.activeTab);
  }

  getMyRequestsForActiveTab(): RequestCardVm[] {
    if (this.activeTab === 'Create') return [];

    return this.getFilteredSortedRequestCards(this.activeTab, 'mine');
  }

  getOthersRequestsForActiveTab(): RequestCardVm[] {
    if (this.activeTab === 'Create') return [];

    return this.getFilteredSortedRequestCards(this.activeTab, 'all');
  }


  private getFilteredSortedRequestCards(
    tab: Exclude<OfftimeTab, 'Create'>,
    scope: OfftimeGroupScope,
  ): RequestCardVm[] {
    const resp = this.getRespFor(tab, scope);
    const rows = [...(resp?.data ?? [])];

    return rows
      .filter((group) => this.matchesOfftimeDateRange(group))
      .sort((a, b) => this.compareOfftimeGroups(a, b))
      .map((group) => ({
        group,
        scope,
        status: tab as RequestStatus,
      }));
  }

  private matchesOfftimeDateRange(group: OfftimeRequestGroupListDto): boolean {
    const filterStart = this.normalizeDateFilter(this.offtimeStartDate);
    const filterEnd = this.normalizeDateFilter(this.offtimeEndDate);

    if (!filterStart && !filterEnd) {
      return true;
    }

    const groupStart = this.getGroupStartDate(group);
    const groupEnd = this.getGroupEndDate(group);

    if (!groupStart || !groupEnd) {
      return false;
    }

    const start = filterStart ? dayjs(filterStart).startOf('day') : null;
    const end = filterEnd ? dayjs(filterEnd).endOf('day') : null;

    if (start && groupEnd.isBefore(start, 'day')) {
      return false;
    }

    if (end && groupStart.isAfter(end, 'day')) {
      return false;
    }

    return true;
  }

  private compareOfftimeGroups(
    a: OfftimeRequestGroupListDto,
    b: OfftimeRequestGroupListDto,
  ): number {
    switch (this.offtimeOrderBy) {
      case 'startDateAsc':
        return this.getDateSortValue(a, 'start') - this.getDateSortValue(b, 'start');

      case 'startDateDesc':
        return this.getDateSortValue(b, 'start') - this.getDateSortValue(a, 'start');

      case 'approvedAtAsc':
        return this.getDateSortValue(a, 'approved') - this.getDateSortValue(b, 'approved');

      case 'approvedAtDesc':
      default:
        return this.getDateSortValue(b, 'approved') - this.getDateSortValue(a, 'approved');
    }
  }

  private getDateSortValue(
    group: OfftimeRequestGroupListDto,
    type: 'start' | 'approved',
  ): number {
    if (type === 'approved') {
      const approvedAt = dayjs(group.approvedAt ?? group.createdAt);
      return approvedAt.isValid() ? approvedAt.valueOf() : 0;
    }

    const start = this.getGroupStartDate(group);
    return start?.valueOf() ?? 0;
  }

  private getGroupStartDate(group: OfftimeRequestGroupListDto): dayjs.Dayjs | null {
    const dates = this.getGroupDateValues(group);
    return dates.length ? dates[0] : null;
  }

  private getGroupEndDate(group: OfftimeRequestGroupListDto): dayjs.Dayjs | null {
    const dates = this.getGroupDateValues(group);
    return dates.length ? dates[dates.length - 1] : null;
  }

  private getGroupDateValues(group: OfftimeRequestGroupListDto): dayjs.Dayjs[] {
    return (group.dayOffRequestDates ?? [])
      .map((day: any) => dayjs(day.date ?? day.startTime ?? day.start))
      .filter((date) => date.isValid())
      .sort((a, b) => a.valueOf() - b.valueOf());
  }

  getActiveTabTitle(): string {
    switch (this.activeTab) {
      case 'Pending':
        return 'Pending Requests';
      case 'Approved':
        return 'Approved Requests';
      case 'Rejected':
        return 'Rejected Requests';
      case 'Cancelled':
        return 'Cancelled Requests';
      case 'Create':
      default:
        return 'Book Off Time';
    }
  }

  getActiveTabSubtitle(): string {
    switch (this.activeTab) {
      case 'Pending':
        return 'Requests awaiting review and approval.';
      case 'Approved':
        return 'All approved off-time requests.';
      case 'Rejected':
        return 'Requests that were not approved.';
      case 'Cancelled':
        return 'Requests that were cancelled.';
      case 'Create':
      default:
        return 'Submit a new off-time request.';
    }
  }

  getRequestInitials(group: OfftimeRequestGroupListDto | OfftimeRequestGroupDetailsDto): string {
    const first = String(group.employee?.firstName ?? '').trim();
    const last = String(group.employee?.lastName ?? '').trim();

    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || '—';
  }

  getRequestEmployeeName(
    group: OfftimeRequestGroupListDto | OfftimeRequestGroupDetailsDto,
    scope?: OfftimeGroupScope,
  ): string {
    const first = String(group.employee?.firstName ?? '').trim();
    const last = String(group.employee?.lastName ?? '').trim();
    const fullName = `${first} ${last}`.trim() || '—';

    const isCurrentUser =
      scope === 'mine' ||
      Number(group.employee?.id) === Number(this.currentUserDetail?.id);

    return isCurrentUser ? `${fullName} (You)` : fullName;
  }

  getRequestActorLine(group: OfftimeRequestGroupListDto, scope: OfftimeGroupScope): string {
    if (scope === 'mine') {
      return 'Self-requested';
    }

    return `Requested by ${this.getRequestedByDisplay(group)}`;
  }

  getRequestDateRange(group: OfftimeRequestGroupListDto | OfftimeRequestGroupDetailsDto): string {
    const dates = (group.dayOffRequestDates ?? [])
      .map((day: any) => this.getDayDateValue(day))
      .filter((date): date is dayjs.Dayjs => !!date && date.isValid())
      .sort((a, b) => a.valueOf() - b.valueOf());

    if (!dates.length) {
      return '—';
    }

    const first = dates[0];
    const last = dates[dates.length - 1];

    if (first.isSame(last, 'day')) {
      return first.format('MMM D');
    }

    return `${first.format('MMM D')} – ${last.format('MMM D')}`;
  }

  getRequestDayCount(group: OfftimeRequestGroupListDto | OfftimeRequestGroupDetailsDto): number {
    return group.dayOffRequestDates?.length ?? 0;
  }

  getRequestDayCountLabel(group: OfftimeRequestGroupListDto | OfftimeRequestGroupDetailsDto): string {
    const count = this.getRequestDayCount(group);
    return `${count} day${count === 1 ? '' : 's'}`;
  }

  getRequestTotalHours(group: OfftimeRequestGroupListDto | OfftimeRequestGroupDetailsDto): number {
    return this.sumRequestDateHours(group.dayOffRequestDates ?? []);
  }

  getRequestApprovedDate(group: OfftimeRequestGroupListDto): string {
    if (group.approvedAt) {
      return `Approved ${dayjs(group.approvedAt).format('MMM D, YYYY')}`;
    }

    return '';
  }

  getRequestCreatedDate(group: OfftimeRequestGroupListDto): string {
    if (group.createdAt) {
      return `Submitted ${dayjs(group.createdAt).format('MMM D, YYYY')}`;
    }

    return '';
  }

  getRequestStatusDate(group: OfftimeRequestGroupListDto, status: RequestStatus): string {
    if (status === 'Approved') {
      return this.getRequestApprovedDate(group);
    }

    if (status === 'Pending') {
      return this.getRequestCreatedDate(group);
    }

    if (status === 'Rejected') {
      return this.getRequestCreatedDate(group);
    }

    return this.getRequestCreatedDate(group);
  }

  getRequestBalanceAvailable(group: OfftimeRequestGroupListDto | OfftimeRequestGroupDetailsDto): number | null {
    return this.getRequestBalanceImpactForGroup(group)?.currentBalance ?? null;
  }

  isRequestUpcoming(group: OfftimeRequestGroupListDto | OfftimeRequestGroupDetailsDto): boolean {
    const today = dayjs().startOf('day');

    return (group.dayOffRequestDates ?? []).some((day: any) => {
      const date = this.getDayDateValue(day);
      return !!date && date.isValid() && this.isSameOrAfterDay(date.startOf('day'), today);
    });
  }

  getRequestTimingLabel(group: OfftimeRequestGroupListDto | OfftimeRequestGroupDetailsDto): 'UPCOMING' | 'PAST' {
    return this.isRequestUpcoming(group) ? 'UPCOMING' : 'PAST';
  }

  private getDayDateValue(day: any): dayjs.Dayjs | null {
    const raw =
      day?.date ??
      day?.startTime ??
      day?.start ??
      null;

    if (!raw) {
      return null;
    }

    const parsed = dayjs(raw);
    return parsed.isValid() ? parsed : null;
  }

  getEditPayCodeName(): string {
    if (!this.editState) return 'Time Off';

    return this.payCodes.find((pc) => pc.id === this.editState?.payCodeId)?.name ?? 'Time Off';
  }

  getEditHoursRequested(): number {
    if (!this.editState) return 0;

    const existingHours = this.editState.days
      .filter((day) => !day.isCancelled)
      .reduce((sum, day) => sum + Math.max(0, day.end.diff(day.start, 'minute')) / 60, 0);

    const addedHours = this.editState.addedDays
      .reduce((sum, day) => sum + Math.max(0, day.end.diff(day.start, 'minute')) / 60, 0);

    return existingHours + addedHours;
  }

  getEditDaysOffCount(): number {
    if (!this.editState) return 0;

    return this.editState.days.filter((day) => !day.isCancelled).length + this.editState.addedDays.length;
  }

  getEditAfterRequestBalance(): number | null {
    const impact = this.getEditBalanceImpact();

    if (!impact) {
      return null;
    }

    return impact.currentBalance - impact.requestedHours;
  }

  getEditSubmittedLine(): string {
    const created = this.editCreatedAt ? dayjs(this.editCreatedAt).format('MMM D, YYYY') : '—';
    return `Requested by ${this.editRequestedByDisplayName} · Submitted ${created}`;
  }

  getEditDayStatusLabel(day: EditDayVm): 'UPCOMING' | 'PAST' {
    return this.isSameOrAfterDay(day.start.startOf('day'), dayjs().startOf('day'))
      ? 'UPCOMING'
      : 'PAST';
  }

  getEditDayDuration(day: EditDayVm): string {
    const hours = Math.max(0, day.end.diff(day.start, 'minute')) / 60;
    return `${hours % 1 === 0 ? hours.toFixed(0) : hours.toFixed(2)}h`;
  }

  trackByGroupId(_: number, item: RequestCardVm): number {
    return item.group.id;
  }

  private preloadRequestCounts(): void {
    forkJoin({
      pending: this.loadTabBothScopes(this.buildListFilter({ isApproved: null }), 1, 1),
      approved: this.loadTabBothScopes(this.buildListFilter({ isApproved: true }), 1, 1),
      rejected: this.loadTabBothScopes(this.buildListFilter({ isApproved: false }), 1, 1),
      cancelled: this.loadTabBothScopes(this.buildListFilter({ isApproved: null, isCanceled: true }), 1, 1),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe(({ pending, approved, rejected, cancelled }) => {
        this.assignTabResponses(this.buildListFilter({ isApproved: null }), pending.mine, pending.allOthers);
        this.assignTabResponses(this.buildListFilter({ isApproved: true }), approved.mine, approved.allOthers);
        this.assignTabResponses(this.buildListFilter({ isApproved: false }), rejected.mine, rejected.allOthers);
        this.assignTabResponses(
          this.buildListFilter({ isApproved: null, isCanceled: true }),
          cancelled.mine,
          cancelled.allOthers,
        );

        this.safeMarkForCheck();
      });
  }

  getRequestTotalForActiveTab(scope: OfftimeGroupScope): number {
    if (this.activeTab === 'Create') return 0;
    return this.getRespFor(this.activeTab, scope)?.totalCount ?? 0;
  }

  getRequestPageInfoForActiveTab(scope: OfftimeGroupScope) {
    if (this.activeTab === 'Create') return null;
    return this.getRespFor(this.activeTab, scope);
  }

  goToRequestPage(
    tab: Exclude<OfftimeTab, 'Create'>,
    scope: OfftimeGroupScope,
    page: number,
  ): void {
    const resp = this.getRespFor(tab, scope);
    const totalPages = Math.max(1, Number(resp?.totalPages ?? 1));
    const nextPage = Math.min(Math.max(1, Number(page)), totalPages);

    switch (tab) {
      case 'Pending':
        scope === 'mine'
          ? (this.pendingMinePage = nextPage)
          : (this.pendingAllPage = nextPage);
        break;

      case 'Approved':
        scope === 'mine'
          ? (this.approvedMinePage = nextPage)
          : (this.approvedAllPage = nextPage);
        break;

      case 'Rejected':
        scope === 'mine'
          ? (this.rejectedMinePage = nextPage)
          : (this.rejectedAllPage = nextPage);
        break;

      case 'Cancelled':
        scope === 'mine'
          ? (this.cancelledMinePage = nextPage)
          : (this.cancelledAllPage = nextPage);
        break;
    }

    this.loadTab(tab);
  }
}